import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BASE_URL } from '@models/global-constant.model';
import { TokenService } from '@services/helpers/token.service';

export type ActivityEntityType =
  | 'lead'
  | 'contact'
  | 'staff'
  | 'company'
  | 'project'
  | 'appointment'
  | 'eoi'
  | 'task'
  | 'listing'
  | 'property'
  | 'lot'
  | 'contract'
  | 'report';

export type ActivityEventType =
  | 'page_view'
  | 'record_open'
  | 'search'
  | 'filter'
  | 'report_view'
  | 'report_export'
  | 'document_download'
  | 'call'
  | 'action';

export interface ActivityRecord {
  entity_type: ActivityEntityType;
  entity_id: string;
}

/** The only `meta` keys the backend keeps — anything else is dropped server-side. Never put user-entered text here. */
export interface ActivityEventMeta {
  report_id?: string;
  format?: 'csv' | 'pdf' | 'xlsx';
  count?: number;
  direction?: string;
  filter_keys?: string[];
  tab?: string;
  module?: string;
}

export interface ActivityEventDetail {
  path?: string;
  project_id?: string | null;
  entity_type?: ActivityEntityType | null;
  entity_id?: string | null;
  action?: string;
  meta?: ActivityEventMeta;
}

interface ActivityPage {
  path: string;
  project_id: string | null;
  /** Shown as the dashboard's "Current Activity" when no record is open. Route names only — never ids or typed text. */
  title: string | null;
}

interface QueuedEvent extends ActivityEventDetail {
  type: ActivityEventType;
  at: number;
}

interface InteractionWindow {
  first: number;
  last: number;
  count: number;
}

const DEFAULT_HEARTBEAT_MS = 60_000;
const CONFIG_RECHECK_MS = 10 * 60_000;
const PAGE_VIEW_DEDUPE_MS = 2000;
const MAX_EVENTS_PER_HEARTBEAT = 50;
const MAX_QUEUED_EVENTS = 200;
const FLUSH_AT_QUEUED_EVENTS = 20;

/** Mouse movement is deliberately absent: an open, untouched tab must read as idle. */
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const PROJECT_PATH = /^\/projects\/(?:edit_\/)?([a-f0-9]{24})(?:\/|$)/i;
/** The part of a project URL after the project id — its tab and sub-tabs. */
const PROJECT_TABS = /^\/projects\/(?:edit_\/)?[a-f0-9]{24}((?:\/[^/]+)*)\/?$/i;
const ACRONYMS = ['eoi', 'sms', 'ai'];

/** Routes that carry user-typed text in the path; the text is cut before the path is reported. */
const FREE_TEXT_ROUTES: RegExp[] = [/^(\/global\/advance-search)\/.*$/];

/** Report screens whose opening counts as a `report_view`; a captured id becomes `meta.report_id`. */
const REPORT_VIEW_ROUTES: RegExp[] = [
  /^\/report\/view-report\/([^/]+)$/,
  // A shared report link opened inside Remmi counts too; the slug is the report's public id.
  /^\/report-view\/([^/]+)$/,
  /^\/report\/(?:overview|kpi-board|company-performance|my-sales|listing-sales|reports|main|kpi)$/,
  /^\/listings\/listing-report$/,
];

/**
 * Live session tracking (docs: USER_ACTIVITY_FRONTEND.md §4). Runs in every authenticated tab and
 * reports *state*, never durations: a heartbeat carries the current page, interaction timestamps
 * relative to the moment of sending, and queued UI events. The server merges tabs and devices and
 * decides Active / Idle / Disconnected / Offline, so nothing here may compute elapsed time.
 *
 * Everything runs outside the Angular zone — the listeners only bump numbers and must never
 * trigger change detection — and every request fails silently: no toast, no retry loop.
 */
@Injectable({ providedIn: 'root' })
export class ActivityTrackerService {
  private readonly baseUrl = `${BASE_URL}/user-activity`;
  private readonly tabId = createTabId();

  private isRunning = false;
  private isEnabled = true;
  private isSending = false;
  private heartbeatMs = DEFAULT_HEARTBEAT_MS;
  private heartbeatTimer: ReturnType<typeof setInterval> = null;
  private flushTimer: ReturnType<typeof setTimeout> = null;
  private configTimer: ReturnType<typeof setTimeout> = null;

  private interaction: InteractionWindow = null;
  private queue: QueuedEvent[] = [];
  private page: ActivityPage = null;
  private record: ActivityRecord = null;
  private liveRecord: () => ActivityRecord | null = null;
  private lastPageViewPath = '';
  private lastPageViewAt = 0;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private tokenService: TokenService
  ) {}

  start(): void {
    if (this.isRunning || typeof document === 'undefined') {
      return;
    }
    this.isRunning = true;
    this.isEnabled = true;
    this.zone.runOutsideAngular(() => {
      INTERACTION_EVENTS.forEach(type => document.addEventListener(type, this.onInteraction, LISTENER_OPTIONS));
      document.addEventListener('click', this.onClick, LISTENER_OPTIONS);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pagehide', this.onPageHide);
    });
    // The first NavigationEnd usually lands before the shell starts the tracker.
    if (this.page) {
      this.trackPageView(this.page.path);
    }
    this.loadConfig();
    this.scheduleHeartbeat();
    this.flushSoon(0);
  }

  /**
   * `timeout` is for forced sign-outs (401, expired token): the beacon ends the session with that
   * reason, so it must go out before storage — and the token with it — is cleared. It goes out even
   * when the tracker never started: the route guard signs out an expired token before the shell
   * loads. A normal logout passes nothing because `POST /auth/logout` already ended the session.
   */
  stop(reason?: 'timeout'): void {
    if (reason) {
      this.sendBeacon(reason);
    }
    if (!this.isRunning) {
      return;
    }
    this.isRunning = false;
    INTERACTION_EVENTS.forEach(type => document.removeEventListener(type, this.onInteraction, LISTENER_OPTIONS));
    document.removeEventListener('click', this.onClick, LISTENER_OPTIONS);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    clearInterval(this.heartbeatTimer);
    clearTimeout(this.flushTimer);
    clearTimeout(this.configTimer);
    this.queue = [];
    this.interaction = null;
    this.page = null;
    this.clearRecord();
    this.lastPageViewPath = '';
  }

  /** Called on every NavigationEnd. Query strings, fragments and typed search text never leave the browser. */
  setPage(url: string, routeTitle: string | null = null): void {
    const rawPath = (url || '/').split('?')[0].split('#')[0] || '/';
    const path = FREE_TEXT_ROUTES.reduce((clean, route) => clean.replace(route, '$1'), rawPath);
    if (path !== this.page?.path) {
      this.clearRecord();
    }
    this.page = { path, project_id: path.match(PROJECT_PATH)?.[1] ?? null, title: pageTitleFor(path, routeTitle) };
    this.trackPageView(path);
  }

  /**
   * A record was opened — drawer tab, deep link or modal. `live` lets the owner report which record
   * is on screen *now*: the drawer's active tab changes and closes in dozens of components, so it is
   * read at send time instead of every close path having to call {@link clearRecord}.
   */
  openRecord(record: ActivityRecord, projectId: string | null = null, live: () => ActivityRecord | null = null): void {
    if (this.trackRecordOpen(record, projectId)) {
      this.record = record;
      this.liveRecord = live;
    }
  }

  /** `record_open` for a record shown inside another one (an appointment in a lead's drawer) — the page keeps reporting the outer record. */
  trackRecordOpen(record: ActivityRecord, projectId: string | null = null): boolean {
    if (!isObjectId(record?.entity_id)) {
      return false;
    }
    this.track('record_open', { ...record, project_id: isObjectId(projectId) ? projectId : (this.page?.project_id ?? null) });
    return true;
  }

  clearRecord(): void {
    this.record = null;
    this.liveRecord = null;
  }

  /** Records a completed UI action. Saves are detected server-side from the API call — never send those here. */
  track(type: ActivityEventType, detail: ActivityEventDetail = {}): void {
    if (!this.isRunning || !this.isEnabled) {
      return;
    }
    this.queue.push({ ...detail, type, at: Date.now() });
    if (this.queue.length > MAX_QUEUED_EVENTS) {
      this.queue.splice(0, this.queue.length - MAX_QUEUED_EVENTS);
    }
    this.markInteraction();
    const isNavigation = type === 'page_view' || type === 'record_open';
    this.flushSoon(this.queue.length >= FLUSH_AT_QUEUED_EVENTS ? 0 : isNavigation ? 1000 : 3000);
  }

  /** Same as {@link track} but attaches the record currently open, for actions that belong to it (calls, downloads). */
  trackOnCurrentRecord(type: ActivityEventType, detail: ActivityEventDetail = {}): void {
    const record = this.currentRecord();
    this.track(type, { ...(record ?? {}), project_id: this.page?.project_id ?? null, ...detail });
  }

  private readonly onInteraction = (event: Event) => {
    if (event.isTrusted) {
      this.markInteraction();
    }
  };

  /** Only the fact that a `tel:` link was clicked is recorded — never the number. */
  private readonly onClick = (event: Event) => {
    const target = event.target as Element;
    if (event.isTrusted && target?.closest?.('a[href^="tel:"]')) {
      this.trackOnCurrentRecord('call');
    }
  };

  private readonly onVisibilityChange = () => this.flushSoon(0);

  private readonly onPageHide = () => this.sendBeacon('closed');

  private trackPageView(path: string): void {
    const now = Date.now();
    if (path === this.lastPageViewPath && now - this.lastPageViewAt < PAGE_VIEW_DEDUPE_MS) {
      return;
    }
    if (!this.isRunning) {
      return;
    }
    this.lastPageViewPath = path;
    this.lastPageViewAt = now;
    this.track('page_view', { path, project_id: this.page?.project_id ?? null });

    const reportRoute = REPORT_VIEW_ROUTES.map(route => path.match(route)).find(Boolean);
    if (reportRoute) {
      this.track('report_view', reportRoute[1] ? { meta: { report_id: reportRoute[1] } } : {});
    }
  }

  private markInteraction(): void {
    const now = Date.now();
    this.interaction = this.interaction ? { ...this.interaction, last: now, count: this.interaction.count + 1 } : { first: now, last: now, count: 1 };
  }

  private currentRecord(): ActivityRecord | null {
    if (!this.record || !this.liveRecord) {
      return this.record;
    }
    try {
      const live = this.liveRecord();
      return isObjectId(live?.entity_id) ? live : null;
    } catch {
      return null;
    }
  }

  private flushSoon(delayMs: number): void {
    clearTimeout(this.flushTimer);
    this.zone.runOutsideAngular(() => {
      this.flushTimer = setTimeout(() => this.sendHeartbeat(), delayMs);
    });
  }

  private scheduleHeartbeat(): void {
    clearInterval(this.heartbeatTimer);
    this.zone.runOutsideAngular(() => {
      this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), this.heartbeatMs);
    });
  }

  private loadConfig(): void {
    this.zone.runOutsideAngular(() => {
      this.http.get<any>(`${this.baseUrl}/config`).subscribe({
        next: response => this.applyTracking(response?.data?.tracking ?? response?.data),
        error: () => {
          if (this.isRunning && !this.isEnabled) {
            this.scheduleConfigRecheck();
          }
        },
      });
    });
  }

  private scheduleConfigRecheck(): void {
    clearTimeout(this.configTimer);
    this.zone.runOutsideAngular(() => {
      this.configTimer = setTimeout(() => this.loadConfig(), CONFIG_RECHECK_MS);
    });
  }

  /** Applies `tracking` from `/config` or a heartbeat response. Disabled = go quiet and re-check `/config` every 10 min. */
  private applyTracking(tracking: any): void {
    if (!tracking || !this.isRunning) {
      return;
    }
    const wasEnabled = this.isEnabled;
    this.isEnabled = tracking.enabled !== false;
    if (!this.isEnabled) {
      clearInterval(this.heartbeatTimer);
      clearTimeout(this.flushTimer);
      this.queue = [];
      this.interaction = null;
      this.scheduleConfigRecheck();
      return;
    }
    const seconds = Math.min(300, Math.max(15, Number(tracking.heartbeat_seconds) || DEFAULT_HEARTBEAT_MS / 1000));
    if (!wasEnabled || seconds * 1000 !== this.heartbeatMs) {
      this.heartbeatMs = seconds * 1000;
      clearTimeout(this.configTimer);
      this.scheduleHeartbeat();
    }
  }

  private sendHeartbeat(): void {
    if (!this.isRunning || !this.isEnabled || this.isSending || !this.tokenService.getToken()) {
      return;
    }
    const now = Date.now();
    const events = this.queue.splice(0, MAX_EVENTS_PER_HEARTBEAT);
    const interaction = this.interaction;
    this.interaction = null;
    const record = this.currentRecord();

    const body = {
      tab_id: this.tabId,
      visible: document.visibilityState === 'visible',
      focused: document.hasFocus(),
      page: this.page ? { ...this.page, entity_type: record?.entity_type ?? null, entity_id: record?.entity_id ?? null } : null,
      interaction: interaction ? { first_ago_ms: now - interaction.first, last_ago_ms: now - interaction.last, count: interaction.count } : null,
      events: events.map(({ at, ...event }) => ({ ...event, ago_ms: now - at })),
    };

    this.isSending = true;
    this.http.post<any>(`${this.baseUrl}/heartbeat`, body).subscribe({
      next: response => {
        this.isSending = false;
        this.applyTracking(response?.data?.tracking);
        if (this.queue.length) {
          this.flushSoon(1000);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSending = false;
        // Offline or server hiccup: hand the payload to the next scheduled heartbeat. The
        // timestamps are absolute, so `ago_ms` stays correct however late it goes out.
        if (this.isRunning && (!error?.status || error.status >= 500)) {
          this.requeue(events, interaction);
        }
      },
    });
  }

  private requeue(events: QueuedEvent[], interaction: InteractionWindow): void {
    this.queue = [...events, ...this.queue].slice(-MAX_QUEUED_EVENTS);
    if (!interaction) {
      return;
    }
    this.interaction = this.interaction
      ? {
          first: Math.min(interaction.first, this.interaction.first),
          last: Math.max(interaction.last, this.interaction.last),
          count: interaction.count + this.interaction.count,
        }
      : interaction;
  }

  /** `text/plain` avoids a CORS preflight, which a beacon cannot make; the token rides in the body because a beacon cannot set headers. */
  private sendBeacon(reason: 'closed' | 'timeout'): void {
    const token = this.tokenService.getToken();
    if (!token || typeof navigator?.sendBeacon !== 'function') {
      return;
    }
    try {
      const body = JSON.stringify({ tab_id: this.tabId, token, reason });
      navigator.sendBeacon(`${this.baseUrl}/tab-closed`, new Blob([body], { type: 'text/plain' }));
    } catch {
      /* page is going away — nothing useful left to do */
    }
  }
}

/**
 * Every tab inside a project shares the route title "Edit Project", so project pages are named from
 * their last route segment instead ("price-list" → "Project price list"). Segments that aren't plain
 * lower-case route names (ids, numbers) are skipped, so nothing record-specific can leak into it.
 */
function pageTitleFor(path: string, routeTitle: string | null): string | null {
  const projectTabs = path.match(PROJECT_TABS)?.[1];
  if (projectTabs !== undefined) {
    const segment = projectTabs
      .split('/')
      .filter(part => /^[a-z][a-z-]*$/.test(part))
      .pop();
    if (!segment) {
      return 'Project overview';
    }
    const words = segment
      .split('-')
      .map(word => (ACRONYMS.includes(word) ? word.toUpperCase() : word))
      .join(' ');
    return (words.startsWith('project') ? words.charAt(0).toUpperCase() + words.slice(1) : `Project ${words}`).slice(0, 150);
  }
  return routeTitle ? String(routeTitle).slice(0, 150) : null;
}

function isObjectId(value: unknown): value is string {
  return typeof value === 'string' && OBJECT_ID.test(value);
}

function createTabId(): string {
  const uuid = typeof crypto !== 'undefined' ? (crypto as any).randomUUID?.() : null;
  return uuid || `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}
